// tslint:disable:max-classes-per-file
import { Action } from '@ngrx/store';
import {
  FormGroupState,
  Unboxed,
  KeyValue,
  SetValueAction,
  NgrxFormControlId,
} from 'ngrx-forms';
import { FormComponent } from './form-component';
import { DxFormState } from './form-state';
import { FireStoreDocument } from '@digitaix/types';

export enum FormsActionTypes {
  SubmitForm = '[FormComponent] Submit Form',
  ResetForm = '[FormComponent] Reset Form',
  SubmitError = '[FormComponent] Submit Error',
  SubmitSuccessful = '[FormComponent] Submit Successful',
  SubmitValid = '[FormComponent] Submit Valid',
  SubmitInvalid = '[FormComponent] Submit Invalid',
  RemoveFormArrayItem = '[FormComponent] Remove Form Array Item',
  AddFormArrayItem = '[FormComponent] Add Form Array Item',
  FormValidationError = '[FormComponent] Form Validation Error',
  RegisterForm = '[FormComponent] Register Form',
  RegisterForms = '[FormComponent] Register Forms',
  SetDocumentId = '[FormComponent] Set Document Id',
  SetFormData = '[FormComponent] Set Form Data',
  OpenCreateDialog = '[FormComponent] Open Create Dialog',
  OpenCreateDialogWithCallBackFormUpdate = '[FormComponent] Open Create Dialog With Call Back Form Update',
  OpenEditDialog = '[FormComponent] Open Edit Dialog',

  CreateFormGroupState = '@digitaix/core/forms/CREATE_FORM_GROUP_STATE'
}

export class DxSetValueAction<T> extends SetValueAction<T> {
  constructor(controlId: NgrxFormControlId, value: T, tracer: string) {
    super(controlId, value);
  }
}

export class ResetForm implements Action {
  public static TYPE = '@digitaix/core/forms/RESET_FORM';
  public readonly type = ResetForm.TYPE;

  constructor(public formId: string, public tracer: string, public stack?: string) {}
}

export class CancelForm implements Action {
  public static TYPE = '@digitaix/core/forms/CANCEL_FORM';
  public readonly type = CancelForm.TYPE;

  constructor(public formId: string, public tracer: string, public stack: string[] = []) {}
}

export class OpenEditFormInWindow implements Action {
  public static TYPE = '@digitaix/core/forms/OPEN_EDIT_FORM_IN_WINDOW';
  public readonly type = OpenEditFormInWindow.TYPE;

  constructor(public formId: string, public documentId: string, public tracer: string, public stack: string[] = []) {}
}

export class OpenCreateFormInWindow<T = any> implements Action {
  public static TYPE = '@digitaix/core/forms/OPEN_CREATE_FORM_IN_WINDOW';
  public readonly type = OpenCreateFormInWindow.TYPE;

  constructor(
    public formId: string,
    public tracer: string,
    public initial: Partial<T> = {},
    public stack: string[] = []
  ) {}
}

export class OpenFormInWindow implements Action {
  public static TYPE = '@digitaix/core/forms/OPEN_FORM_IN_WINDOW';
  public readonly type = OpenFormInWindow.TYPE;

  constructor(public formId: string, public tracer: string, public stack: string[] = []) {}
}

export class MarkAsPreSubmitted implements Action {
  public static TYPE = '@digitaix/core/forms/MARK_AS_PRE_SUBMITTED';
  public readonly type = MarkAsPreSubmitted.TYPE;

  constructor(public formId: string) {}
}

export class InitForm implements Action {
  public static TYPE = '@digitaix/core/forms/INIT_FORM';
  public readonly type = InitForm.TYPE;

  constructor(public formId: string) {}
}

export class DestroyForm implements Action {
  public static TYPE = '@digitaix/core/forms/DESTROY_FORM';
  public readonly type = DestroyForm.TYPE;

  constructor(public formId: string) {}
}

export class CreateFormGroupState<FormValue extends KeyValue>
  implements Action {
  public readonly type = FormsActionTypes.CreateFormGroupState;

  constructor(public formId: string, public initialValue: FormValue) {}
}

export class SetFormData<F = any> implements Action {
  public readonly type = FormsActionTypes.SetFormData;

  constructor(public formId: string, public data: F) {}
}

export class SubmitValid<F = any> implements Action {
  public readonly type = FormsActionTypes.SubmitValid;

  constructor(
    public formId: string,
    public value: F,
    public collectionPath?: string
  ) {}
}

export class SubmitInvalid<F = any, E = any> implements Action {
  public readonly type = FormsActionTypes.SubmitInvalid;

  constructor(public formId: string, public value: F, public errors: E) {}
}

export class OpenCreateDialogWithCallBackFormUpdate<
  TargetForm = any,
  Form = any,
  Result extends FireStoreDocument = any
> implements Action {
  public readonly type =
    FormsActionTypes.OpenCreateDialogWithCallBackFormUpdate;

  constructor(
    public formId: string,
    public initial: Partial<Unboxed<TargetForm>>,
    public callbackFormId: string,
    public createPath: (value: Result) => Partial<Unboxed<Form>>
  ) {}
}

export class OpenCreateDialog<Form = any> implements Action {
  public readonly type = FormsActionTypes.OpenCreateDialog;

  constructor(
    public formId: string,
    public initial?: Partial<Form>,
    public tracer?: string
  ) {}
}

export class OpenCreateRoute<Form = any> implements Action {
  public static TYPE = '@digitaix/core/forms/OPEN_CREATE_ROUTE';
  public readonly type = OpenCreateRoute.TYPE;

  constructor(
    public formId: string,
    public initial?: Partial<Form>,
    public tracer?: string
  ) {}
}

export class OpenEditRoute implements Action {
  public static TYPE = '@digitaix/core/forms/OPEN_EDIT_ROUTE';
  public readonly type = OpenEditRoute.TYPE;

  constructor(
    public formId: string,
    public documentId: string,
    public tracer?: string
  ) {}
}

export class OpenEditDialog implements Action {
  public readonly type = FormsActionTypes.OpenEditDialog;

  constructor(
    public formId: string,
    public documentId: string,
    public tracer?: string
  ) {}
}

export class RegisterForms implements Action {
  public readonly type = FormsActionTypes.RegisterForms;

  constructor(
    public forms: {
      [formId: string]: {
        initial: any;
        arrayInitial?: any;
        collectionPath: string;
        unbox?: (formValue: any) => any;
        box?: (rawForm: any) => any;
        validateAndUpdateForm?: (
          state: FormGroupState<any>
        ) => FormGroupState<any>;
        editComponent?: new (...args: any[]) => FormComponent<any>;
      };
    }
  ) {}
}

export class RegisterForm<Form, RawForm> implements Action {
  public readonly type = FormsActionTypes.RegisterForm;

  constructor(
    public formId: string,
    public payload: Partial<DxFormState<Form, RawForm>>
  ) {}
}

export class RemoveFormArrayItem implements Action {
  public readonly type = FormsActionTypes.RemoveFormArrayItem;

  constructor(
    public formId: string,
    public path: string,
    public index: number
  ) {}
}

export class AddFormArrayItem implements Action {
  public readonly type = FormsActionTypes.AddFormArrayItem;

  constructor(public formId: string, public path: string) {}
}

export class SetDocumentId implements Action {
  public readonly type = FormsActionTypes.SetDocumentId;

  constructor(public formId: string, public documentId: string) {}
}

export class FormValidationError implements Action {
  public readonly type = FormsActionTypes.FormValidationError;

  constructor(public formId: string, public error: any) {}
}

export class SubmitError implements Action {
  public readonly type = FormsActionTypes.SubmitError;

  constructor(public formId: string, public error: any) {}
}

export class SubmitSuccessful<D = any> implements Action {
  public readonly type = FormsActionTypes.SubmitSuccessful;

  constructor(public formId: string, public result: D = null, public tracer: string) {}
}

export type FormsActions<Form, RawForm> =
  | RemoveFormArrayItem
  | AddFormArrayItem
  | SubmitSuccessful
  | OpenCreateDialogWithCallBackFormUpdate
  | SetDocumentId
  | OpenCreateDialog
  | OpenEditDialog
  | RegisterForm<Form, RawForm>
  | FormValidationError
  | RegisterForms
  | SubmitError;
